import React, { useState } from 'react'

import { Typography } from '@telavita-core/react-design-kit'

import { toDayMonthYear } from 'utils/dateTimeHandlers'
import { format } from 'date-fns'

import { ScheduleContainerFrequencyOptions } from 'containers/ScheduleContainer/_settings'

import { CoreIcons } from 'components'
import { PlanDetail } from 'components/PlanDetail/PlanDetail'

interface CorporateEligibilityProps {
  isEligible: boolean
  isCorporateLimited: boolean
  availableCredits: string | number
  maxCredits: string | number
  expirationDate: string
  dateCreated: string
  frequency: string
  planName: string
  planGroupName: string
  isActive: boolean
  inactivatePlanInfo?: {
    inactivatedAt: string
    inactivatedBy: string
    moreInfo: string
    reason: string
  }
  showPlanDetails?: boolean
}

const CorporateEligibility = ({
  availableCredits,
  dateCreated,
  expirationDate,
  isCorporateLimited,
  isEligible,
  frequency,
  maxCredits,
  planName,
  planGroupName,
  isActive,
  inactivatePlanInfo,
  showPlanDetails = false
}: CorporateEligibilityProps): JSX.Element => {

  const [openDetail, setOpenDetail] = useState(false)


  const getFrequency = () => {
    if (frequency === ScheduleContainerFrequencyOptions.WEEKLY) {
      return 'semana'
    } else if (
      frequency === ScheduleContainerFrequencyOptions.BIWEEKLY
    ) {
      return '2 semanas'
    } else if (
      frequency === ScheduleContainerFrequencyOptions.QUARTERLY
    ) {
      return '12 semanas'
    }
  }

  const setConditionalClass = () => {
    if (isEligible) {
      if (isCorporateLimited) {
        if (availableCredits) {
          if (new Date() < new Date(expirationDate)) {
            return {
              message: '',
              className: 'eligible',
            }
          } else
            return {
              message: '*A validade do(s) crédito(s) de consulta expirou.',
              className: 'expirationDate',
            }
        } else
          return {
            message: '*Não há mais consultas disponíveis pelo convênio.',
            className: 'unavailable',
          }
      } else
        return {
          message:
            'Agendamentos liberados em todos os meses disponíveis no calendário.',
          className: 'corporateUnlimited',
        }
    } else
      return {
        message: (
          <>
            O CPF do paciente não consta mais na lista de beneficiários do
            convênio. <strong>Se houver algum engano</strong>, peça para ele
            entrar em contato com o responsável pelo convênio para mais
            informações.
          </>
        ),
        className: isCorporateLimited ? 'ineligible' : 'ineligibleUnlimited',
      }
  }

  const renderMessage = () => {
    const result = setConditionalClass()

    if (!result.message) return

    return (
      <div
        className={`ContainerPatientDetail__planGroup__info ${result.className}`}
      >
        <Typography variant='content3' color='danger'>
          {result.message}
        </Typography>
      </div>
    )
  }

  const renderEligibilityInfo = () => {
    if (isCorporateLimited) {
      return (
        <>
          <ul className='ContainerPatientDetail__planGroup__list'>
            <li>
              <Typography variant='content2'>Consultas disponíveis:</Typography>
              <Typography
                variant='content2'
                weight='bolder'
                customClassName={`credits--${setConditionalClass().className}`}
              >
                {availableCredits} de{' '}
                {maxCredits}
              </Typography>
            </li>
            <li>
              <Typography variant='content2'>Válidas até:</Typography>
              <Typography
                variant='content2'
                customClassName={`validity--${setConditionalClass().className}`}
              >
                {toDayMonthYear(expirationDate)}
              </Typography>
            </li>
            <li>
              <Typography variant='content2'>
                Frequência de agendamento
              </Typography>
              <Typography variant='content2'>
                1 a cada {getFrequency()}
              </Typography>
            </li>
            <li>
              <Typography variant='content2'>Criado em: </Typography>
              <Typography variant='content2'>
                {toDayMonthYear(dateCreated)}
              </Typography>
            </li>
          </ul>
          {renderMessage()}
        </>
      )
    } else if (!isCorporateLimited) {
      return (
        <>
          <ul className='ContainerPatientDetail__planGroup__list'>
            <li>
              <Typography variant='content2'>Válidas até:</Typography>
              <Typography
                variant='content2'
                customClassName={`validity--${setConditionalClass().className}`}
              >
                <i>indeterminado</i>
              </Typography>
            </li>
            <li>
              <Typography variant='content2'>Tem direito a:</Typography>
              <Typography variant='content2'>
                1 a cada {getFrequency()}
              </Typography>
            </li>
            <li>
              <Typography variant='content2'>Criado em: </Typography>
              <Typography variant='content2'>
                {toDayMonthYear(dateCreated)}
              </Typography>
            </li>
          </ul>
          {renderMessage()}
        </>
      )
    }
  }

  return (
    <div
      className={`ContainerPatientDetail__planGroup planGroupInfo__${
        setConditionalClass().className
      }`}
    >
      <div className='ContainerPatientDetail__planInfo'>
        <Typography weight='bolder' variant='content1'>
          Convênio {planGroupName} {planName}
        </Typography>
        <Typography
          variant='content2'
          customClassName='ContainerPatientDetail__planInfo__status'
          inlineStyles={{
            color: isActive ? '#4AC326' : '#FC4949'
          }}
        >
          <CoreIcons
            fill={isActive ? '#4AC326' : '#FC4949'}
            height='13px'
            name='StatusIcon'
            width='13px'
          />
          Convênio {isActive ? 'ativo' : 'inativo'}
        </Typography>
      </div>
      {renderEligibilityInfo()}
      {showPlanDetails && (
        <PlanDetail
          reasonTitle='Motivo da inativação'
          headerResume={
            <>
              Convênio inativado em<br/>
              {format(new Date(inactivatePlanInfo.inactivatedAt), 'dd/MM/yyyy')}, às {format(new Date(inactivatePlanInfo.inactivatedAt), 'HH\'h\'mm')}<br/>
              Por {inactivatePlanInfo.inactivatedBy}
            </>
          }
          toggleDetail={() => setOpenDetail(prevState => !prevState)}
          displayDetail={openDetail}
          reason={inactivatePlanInfo.reason}
          moreInfo={inactivatePlanInfo.moreInfo}
        />
      )}
    </div>
  )
}

export default CorporateEligibility